/* eslint-disable no-bitwise */

export type PermissionScope = 'curator' | 'vault'

export type PermissionDefinition = {
  bit: bigint
  isOwnerPermission?: boolean
  label: string
}

export type PermissionCatalog = readonly PermissionDefinition[]

const OWNER = 1n << 255n
const ADMIN = 1n << 254n
const MEMBER = 1n << 253n

export const CURATOR_PERMISSION = {
  ADMIN,
  FINANCE_MANAGER: 1n << 1n,
  MEMBER,
  OWNER,
  VAULT_CREATOR: 1n << 0n,
} as const

export const VAULT_PERMISSION = {
  ADMIN,
  ASSET_MANAGER: 1n << 0n,
  CONFIG_MANAGER: 1n << 2n,
  CUSTOMER_MANAGER: 1n << 3n,
  MEMBER,
  OWNER,
  SETTLE_MANAGER: 1n << 1n,
} as const

export const CURATOR_PERMISSION_CATALOG: PermissionDefinition[] = [
  { bit: CURATOR_PERMISSION.MEMBER, label: 'Member' },
  { bit: CURATOR_PERMISSION.VAULT_CREATOR, label: 'Vault creator' },
  { bit: CURATOR_PERMISSION.FINANCE_MANAGER, label: 'Finance manager' },
  { bit: CURATOR_PERMISSION.ADMIN, label: 'Admin' },
  { bit: CURATOR_PERMISSION.OWNER, isOwnerPermission: true, label: 'Owner' },
]

export const VAULT_PERMISSION_CATALOG: PermissionDefinition[] = [
  { bit: VAULT_PERMISSION.MEMBER, label: 'Member' },
  { bit: VAULT_PERMISSION.ASSET_MANAGER, label: 'Asset manager' },
  { bit: VAULT_PERMISSION.SETTLE_MANAGER, label: 'Settlement manager' },
  { bit: VAULT_PERMISSION.CONFIG_MANAGER, label: 'Configuration manager' },
  { bit: VAULT_PERMISSION.CUSTOMER_MANAGER, label: 'Customer manager' },
  { bit: VAULT_PERMISSION.ADMIN, label: 'Admin' },
  { bit: VAULT_PERMISSION.OWNER, isOwnerPermission: true, label: 'Owner' },
]

export const getPermissionCatalog = (scope: PermissionScope) =>
  scope === 'curator' ? CURATOR_PERMISSION_CATALOG : VAULT_PERMISSION_CATALOG

export const normalizePermissionMask = (value: bigint | null | string | undefined): bigint => {
  if (value === null || value === undefined || value === '') return 0n

  return typeof value === 'bigint' ? value : BigInt(value)
}

export const hasPermission = (mask: bigint, bit: bigint) => (mask & bit) === bit

export const hasAllPermissions = (mask: bigint, bits: readonly bigint[]) =>
  bits.every((bit) => hasPermission(mask, bit))

export const removesOwnOwnerPermission = ({
  currentMask,
  expectedMask,
  isSelf,
  ownerPermission,
}: {
  currentMask: bigint
  expectedMask: bigint
  isSelf: boolean
  ownerPermission: bigint
}) => isSelf && hasPermission(currentMask, ownerPermission) && !hasPermission(expectedMask, ownerPermission)

export const hasAnyPermission = (mask: bigint, bits: readonly bigint[]) => bits.some((bit) => hasPermission(mask, bit))

export const togglePermission = (mask: bigint, bit: bigint, enabled: boolean) => (enabled ? mask | bit : mask & ~bit)

export const countPermissions = (mask: bigint, catalog: PermissionCatalog) =>
  catalog.filter(({ bit }) => hasPermission(mask, bit)).length

export const isCuratorMember = (mask: bigint) => hasPermission(mask, CURATOR_PERMISSION.MEMBER)

export const canSettleRequests = (mask: bigint | undefined, hasSignedRequests: boolean) =>
  mask !== undefined &&
  hasAllPermissions(
    mask,
    hasSignedRequests
      ? [VAULT_PERMISSION.MEMBER, VAULT_PERMISSION.SETTLE_MANAGER, VAULT_PERMISSION.CUSTOMER_MANAGER]
      : [VAULT_PERMISSION.SETTLE_MANAGER],
  )
